import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { StepProps } from '../../../components/_WMS/Stepper'
import { RelocateState } from '../types'
import { Product } from '../../../interfaces/Producto/Product'
import { ApiResponse } from '../../../typings/ApiResponse'
import { ApiConfig } from '../../../config/api'
import axios from 'axios'
import { useProductOrigins } from '../../../hooks'
import { useApi } from '../../../hooks/useApi'
import { getStepperContainerHeight } from '../../../utils/helpers'
import { State } from '../../../components/_EOS/State'
import { Status, State as IState } from '../../../hooks/useFetchState'
import CustomAlert from '../../../components/_WMS/AlertMessage/AlertMessage'

interface SkuAndBrandStepProps extends StepProps<RelocateState> {}

const getProductsBySku = (sku: string) => {
  return axios.get<ApiResponse<Product>>(
    `${ApiConfig.StockKeepingUnitApi}?sku=${sku}`
  )
}

const getProductsByEan = (ean: string) => {
  return axios.get<ApiResponse<Product>>(
    `${ApiConfig.StockKeepingUnitApi}?ean13=${ean}`
  )
}

export const SkuAndBrandStep: React.FC<SkuAndBrandStepProps> = ({
  next,
  sharedState,
  setSharedState
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [localSku, setLocalSku] = useState(sharedState.sku)
  const [availableProducts, setAvailableProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [brandSearchState, setBrandSearchState] = useState<IState>({
    status: Status.Idle
  })
  const [skuError, setSkuError] = useState(false)

  const skuInputRef = useRef<HTMLInputElement>(null)
  const continueButtonRef = useRef<HTMLButtonElement>(null)

  const {
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  const { callApi } = useApi<ApiResponse<Product>>()

  // Focus en SKU al montar el componente
  useEffect(() => {
    if (skuInputRef.current) {
      skuInputRef.current.focus()
    }
  }, [])

  const handleSkuSearch = async () => {
    if (!localSku.trim()) {
      setBrandSearchState({
        status: Status.Error,
        error: 'Código SKU/EAN no reconocido'
      })
      setSkuError(true)
      return
    }

    setIsLoading(true)
    setBrandSearchState({ status: Status.Idle })
    setSkuError(false)

    // Reset all data from step 2 when searching new SKU
    setSharedState((prev) => ({
      ...prev,
      productOriginId: null,
      productOriginName: '',
      posicionOrigen: '',
      cantidad: null,
      posicionDestino: '',
      availableStock: undefined,
      originPositionId: undefined,
      destinyPositionId: undefined
    }))

    try {
      let response = await callApi(getProductsBySku(localSku.trim()))

      if (response.items.length === 0) {
        response = await callApi(getProductsByEan(localSku.trim()))
      }
      if (response.items.length === 0) {
        setAvailableProducts([])
        setBrandSearchState({
          status: Status.Error,
          error: 'No se encontraron productos para este código SKU/EAN'
        })
        setSkuError(true)
      } else {
        setAvailableProducts(response.items)
        setSharedState((prev) => ({ ...prev, sku: localSku.trim() }))

        // Mostrar loading mientras se procesan las marcas
        setBrandSearchState({ status: Status.Loading })

        setTimeout(() => {
          if (response.items.length === 1) {
            // Auto-seleccionar si solo hay una marca disponible
            const singleProduct = response.items[0]
            const selectedBrand = productOrigins?.find(
              (brand) => brand.productOriginId === singleProduct.productOriginId
            )

            setSharedState((prev) => ({
              ...prev,
              productOriginId: singleProduct.productOriginId,
              productOriginName: selectedBrand?.name || '',
              isGreenSale: singleProduct.isGreenSale || false,
              hasBatchManagement: singleProduct.batch || false,
              hasExpirationDate: singleProduct.expirationDate || false,
              expirationDays: selectedBrand?.expirationDays || 0
            }))

            // Avanzar automáticamente después de un breve delay
            setTimeout(() => {
              setBrandSearchState({ status: Status.Success })
              next()
            }, 1200)
          } else {
            // Múltiples marcas: mostrar dropdown
            setBrandSearchState({
              status: Status.Success,
              data: { showDropdown: true }
            })
          }
        }, 800)
      }
    } catch (err) {
      setAvailableProducts([])
      setBrandSearchState({
        status: Status.Error,
        error: 'Error al buscar el código SKU/EAN'
      })
      setSkuError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkuChange = (value: string) => {
    setLocalSku(value)
    // Reset error state when user starts typing
    if (skuError) {
      setSkuError(false)
      setBrandSearchState({ status: Status.Idle })
    }
  }

  const handleBrandSelect = (productOriginId: number) => {
    const selectedBrand = productOrigins?.find(
      (brand) => brand.productOriginId === productOriginId
    )

    const selectedProduct = availableProducts.find(
      (product) => product.productOriginId === productOriginId
    )

    setSharedState((prev) => ({
      ...prev,
      productOriginId,
      productOriginName: selectedBrand?.name || '',
      hasBatchManagement: selectedProduct?.batch || false,
      hasExpirationDate: selectedProduct?.expirationDate || false,
      expirationDays: selectedBrand?.expirationDays || 0
    }))

    // Focus en el botón Continuar después de seleccionar marca
    setTimeout(() => {
      if (continueButtonRef.current) {
        continueButtonRef.current.focus()
      }
    }, 100)
  }

  const handleNext = () => {
    if (!sharedState.sku || !sharedState.productOriginId) {
      setBrandSearchState({
        status: Status.Error,
        error: 'Complete todos los campos antes de continuar'
      })
      return
    }

    // Reset position and quantity data when proceeding to next step
    setSharedState((prev) => ({
      ...prev,
      posicionOrigen: '',
      cantidad: null,
      posicionDestino: '',
      availableStock: undefined,
      originPositionId: undefined,
      destinyPositionId: undefined
    }))

    next()
  }

  const isContinueVisible =
    availableProducts.length > 0 &&
    brandSearchState.status === Status.Success &&
    brandSearchState.data?.showDropdown

  const canProceed =
    sharedState.sku &&
    sharedState.productOriginId &&
    availableProducts.length > 0 &&
    brandSearchState.status === Status.Success &&
    brandSearchState.data?.showDropdown

  return (
    <Box
      style={{
        height: isMobile ? '100%' : getStepperContainerHeight(),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Box p={3} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant='h5' sx={{ mb: 1, textAlign: 'center' }}>
          Seleccionar Producto
        </Typography>

        {/* SKU Input */}
        <Box
          mb={1}
          mt={isMobile ? 1 : 8}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 'none',
            flexDirection: 'column'
          }}
        >
          <Box
            display='flex'
            alignItems='flex-start'
            flexDirection={isMobile ? 'column' : 'row'}
            style={{ width: '100%', maxWidth: '600px' }}
          >
            <TextField
              inputRef={skuInputRef}
              label='SKU/EAN'
              variant='outlined'
              fullWidth
              value={localSku}
              onChange={(e) => handleSkuChange(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSkuSearch()
                }
              }}
              error={skuError}
              disabled={isLoading || brandSearchState.status === Status.Loading}
              style={{
                marginRight: isMobile ? 0 : 16,
                ...(skuError && {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#f44336',
                      borderWidth: '2px'
                    }
                  }
                }),
                ...(availableProducts.length > 0 &&
                  !skuError && {
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#4caf50',
                        borderWidth: '2px'
                      }
                    }
                  })
              }}
            />
            {!(
              brandSearchState.status === Status.Success &&
              brandSearchState.data?.showDropdown
            ) && (
              <Button
                variant='contained'
                color='primary'
                onClick={handleSkuSearch}
                disabled={isLoading || !localSku.trim()}
                fullWidth={isMobile}
                style={{
                  minWidth: isMobile ? 'auto' : '100px',
                  height: '56px'
                }}
              >
                {isLoading ? 'Buscando...' : 'Buscar'}
              </Button>
            )}
          </Box>

          {/* SKU Error Message */}
          {skuError && brandSearchState.status === Status.Error && (
            <CustomAlert
              severity='error'
              message={brandSearchState.error}
              maxWidth={600}
            />
          )}

          {/* Brand Search State */}
          {availableProducts.length > 0 && (
            <Box mt={3} style={{ width: '100%', maxWidth: '600px' }}>
              <State
                state={brandSearchState}
                loadingMessage='Consultando marcas disponibles...'
              >
                {brandSearchState.data?.showDropdown && (
                  <FormControl variant='outlined' fullWidth>
                    <InputLabel>Marca</InputLabel>
                    <Select
                      value={sharedState.productOriginId || ''}
                      onChange={(e) =>
                        handleBrandSelect(Number(e.target.value))
                      }
                      label='Marca'
                      disabled={brandSearchState.status === Status.Loading}
                    >
                      {availableProducts.map((product) => {
                        const brand = productOrigins?.find(
                          (b) => b.productOriginId === product.productOriginId
                        )
                        return (
                          <MenuItem
                            key={product.productOriginId}
                            value={product.productOriginId}
                          >
                            {brand?.name || `ID: ${product.productOriginId}`}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                )}
              </State>
            </Box>
          )}
        </Box>

        {/* Next Button */}
        {isContinueVisible && (
            <Box display='flex' justifyContent='flex-end'>
              <Button
                ref={continueButtonRef}
                variant='contained'
                color='primary'
                onClick={handleNext}
                disabled={!canProceed}
                size='large'
                style={{
                  backgroundColor: canProceed
                    ? theme.palette.success.main
                    : undefined,
                  color: canProceed ? '#fff' : undefined
                }}
              >
                Continuar
              </Button>
            </Box>
          )}
      </Box>
    </Box>
  )
}
